import type { ForecastVersion } from "../types"
import { can_manage_forecast_versions, is_forecast_admin } from "./version_access"

/**
 * The approval queue, client side. Mirrors the server's `hierarchy_save_must_queue`: a sales rep's
 * (or broker's) save to the primary or a locked version is held for an org admin instead of being
 * written; an admin's save, and a rep's save to any other version, applies directly. The UI uses
 * this to word the save button and the confirm dialog; the server decides for real.
 */
export function save_requires_approval(
  user: { role?: string } | null | undefined,
  version: Pick<ForecastVersion, "is_primary" | "locked_at"> | null | undefined,
  approvals_required: boolean
): boolean {
  if (!version) return false
  if (can_manage_forecast_versions(user, approvals_required)) return false
  return Boolean(version.is_primary) || Boolean(version.locked_at)
}

/** What the server did with a hierarchy save. */
export type HierarchySaveOutcome = { kind: "applied"; updated_at: string } | { kind: "queued"; change_request_id: string | null }

/**
 * Reads the save response: `queued: true` means nothing was written and the edits sit in the
 * approval queue (the grid must keep them, not reload); otherwise the version moved to `updated_at`.
 */
export function hierarchy_save_outcome(response: {
  updated_at: string
  queued?: boolean
  change_request_id?: string | null
}): HierarchySaveOutcome {
  if (response.queued) {
    return { kind: "queued", change_request_id: response.change_request_id ?? null }
  }
  return { kind: "applied", updated_at: response.updated_at }
}

export type ChangeRequestStatus = "pending" | "approved" | "rejected"

/** The header button's label: a count for admins, "your N pending changes" for a rep. */
export function pending_changes_label(user: { role?: string } | null | undefined, pending_count: number): string {
  if (is_forecast_admin(user)) {
    return pending_count === 1 ? "1 pending approval" : `${pending_count} pending approvals`
  }
  return pending_count === 1 ? "1 change awaiting approval" : `${pending_count} changes awaiting approval`
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  const rep = { role: "user" }
  const broker = { role: "broker" }
  const admin = { role: "org_admin" }

  describe("save_requires_approval", () => {
    it("holds a rep's save to the primary or a locked version", () => {
      expect(save_requires_approval(rep, { is_primary: true, locked_at: undefined }, true)).toBe(true)
      expect(save_requires_approval(rep, { is_primary: false, locked_at: "2026-10-01T00:00:00Z" }, true)).toBe(true)
      expect(save_requires_approval(broker, { is_primary: true, locked_at: undefined }, true)).toBe(true)
    })

    it("lets a rep's save to any other version, and every admin save, apply directly", () => {
      expect(save_requires_approval(rep, { is_primary: false, locked_at: undefined }, true)).toBe(false)
      expect(save_requires_approval(admin, { is_primary: true, locked_at: "2026-10-01T00:00:00Z" }, true)).toBe(false)
      expect(save_requires_approval(rep, null, true)).toBe(false)
    })

    it("holds nothing in an org that has not switched governance on", () => {
      expect(save_requires_approval(rep, { is_primary: true, locked_at: undefined }, false)).toBe(false)
      expect(save_requires_approval(broker, { is_primary: false, locked_at: "2026-10-01T00:00:00Z" }, false)).toBe(false)
    })
  })

  describe("hierarchy_save_outcome", () => {
    it("reads a queued save as queued and anything else as applied", () => {
      expect(hierarchy_save_outcome({ updated_at: "t", queued: true, change_request_id: "r1" })).toEqual({
        kind: "queued",
        change_request_id: "r1",
      })
      expect(hierarchy_save_outcome({ updated_at: "t" })).toEqual({ kind: "applied", updated_at: "t" })
      expect(hierarchy_save_outcome({ updated_at: "t", queued: false })).toEqual({ kind: "applied", updated_at: "t" })
    })
  })

  describe("pending_changes_label", () => {
    it("counts approvals for an admin and the rep's own changes otherwise", () => {
      expect(pending_changes_label(admin, 1)).toBe("1 pending approval")
      expect(pending_changes_label(admin, 3)).toBe("3 pending approvals")
      expect(pending_changes_label(rep, 2)).toBe("2 changes awaiting approval")
    })
  })
}
